<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { toasts, dismissToast } from '@/lib/toast'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-vue-next'

const { t } = useI18n()

const kindClass = {
  info: 'bg-card',
  warning: 'bg-card',
  error: 'bg-card',
  success: 'bg-card',
} as const

const iconOf = {
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
  success: CheckCircle2,
} as const

const iconColor = {
  info: 'text-muted-foreground',
  warning: 'text-status-warning',
  error: 'text-destructive',
  success: 'text-status-completed',
} as const
</script>

<template>
  <Teleport to="body">
    <div
      v-if="toasts.toasts.length"
      class="fixed top-4 right-4 left-4 sm:left-auto z-[100] flex flex-col gap-2 sm:max-w-sm pointer-events-none"
      role="region"
      :aria-label="t('common.notifications')"
    >
      <div
        v-for="toast in toasts.toasts"
        :key="toast.id"
        class="pointer-events-auto flex items-start gap-3 rounded-card ring-1 ring-border shadow-[0_8px_32px_-8px_rgb(18_20_23/0.18)] p-4 animate-in slide-in-from-right-2"
        :class="kindClass[toast.kind]"
        role="status"
        aria-live="polite"
      >
        <component
          :is="iconOf[toast.kind]"
          class="h-4 w-4 shrink-0 mt-0.5"
          :class="iconColor[toast.kind]"
        />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium">
            {{ toast.title }}
          </p>
          <p
            v-if="toast.message"
            class="text-xs text-muted-foreground mt-0.5"
          >
            {{ toast.message }}
          </p>
          <p
            v-if="typeof toast.retryAfter === 'number'"
            class="text-xs text-muted-foreground mt-1 font-mono"
          >
            {{ $t('common.retry_in', { s: toast.retryAfter }) }}
          </p>
        </div>
        <button
          class="text-muted-foreground hover:text-foreground shrink-0"
          :aria-label="$t('common.close')"
          @click="dismissToast(toast.id)"
        >
          <X class="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  </Teleport>
</template>
